import { useEffect, useState } from 'react';

// ─── useBrandScrollProgress ────────────────────────────────────────
// Subtle scroll-aware dimming for the header brand. As the user
// scrolls deeper into their bookmark list the wordmark + greeting
// fade and shrink slightly, letting the content breathe and giving
// the surface a "premium" feel without consuming extra real estate.
//
// Returns a `progress` value clamped 0→1. We throttle the scroll
// handler with rAF so even on a 5,000-bookmark list it stays smooth.
// Sub-pixel updates (<0.01 delta) are skipped to keep repaints cheap.
export function useBrandScrollProgress() {
    const [progress, setProgress] = useState(0);
    useEffect(() => {
        const FADE_START = 60;   // px before any change kicks in
        const FADE_END = 240;    // px at which brand reaches min opacity/scale
        let raf = 0;
        let lastProgress = 0;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const y = window.scrollY || document.documentElement.scrollTop || 0;
                const t = Math.max(0, Math.min(1, (y - FADE_START) / (FADE_END - FADE_START)));
                if (Math.abs(t - lastProgress) > 0.01) {
                    lastProgress = t;
                    setProgress(t);
                }
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);
    return progress;
}
